<template>
  <div class="privacy-page">
    <!-- Contenido principal -->
    <div class="main-content">
      <!-- Estado de carga -->
      <div
        v-if="isLoading"
        class="content-card"
      >
        <div class="pa-8 text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
            :aria-label="t('common.cargando')"
          />
          <p class="mt-4 text-body-1 text-grey-darken-1">
            {{ t("common.cargandoContenido") }}
          </p>
        </div>
      </div>

      <!-- Contenido HTML -->
      <div
        v-else-if="appStore.pageHtml"
        class="content-card"
      >
        <article
          class="dynamic-content"
          :aria-label="t('common.contenidoPagina')"
          v-html="appStore.pageHtml"
        />
      </div>

      <!-- Estado de error -->
      <div
        v-else
        class="content-card"
      >
        <div class="pa-8 text-center">
          <v-icon
            size="64"
            color="grey-lighten-1"
            class="mb-4"
          >
            mdi-alert-circle-outline
          </v-icon>
          <p class="text-h6 text-grey-darken-1 mb-2">
            {{ t("common.sinContenido") }}
          </p>
          <p class="text-body-2 text-grey">
            {{ t("common.errorCarga") }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useAppStore } from "@/stores/app.store";
import { useI18n } from "vue-i18n";
import { computed, onMounted, onBeforeUnmount, nextTick } from "vue";

// ========== COMPOSABLES ==========
const appStore = useAppStore();
const { t, locale } = useI18n();

// ========== COMPUTED ==========
const isLoading = computed(() => !appStore.pageHtml && appStore.isLoadingPage);

// ========== CLEANUP REFS ==========
const internalLinkHandlers = [];
const timeoutIds = [];

// ========== LIFECYCLE HOOKS ==========
onMounted(async () => {
  await nextTick();
  enhanceExternalLinks();
  handleInternalLinks();
});

onBeforeUnmount(() => {
  // Limpiar timeouts
  timeoutIds.forEach((id) => clearTimeout(id));
  timeoutIds.length = 0;

  // Limpiar event listeners
  internalLinkHandlers.forEach(({ element, handler }) => {
    element.removeEventListener("click", handler);
  });
  internalLinkHandlers.length = 0;
});

// ========== MÉTODOS ==========
/**
 * Mejora la accesibilidad de los enlaces externos
 */
const enhanceExternalLinks = () => {
  if (typeof document === "undefined") return;
  const content = document.querySelector(".dynamic-content");
  if (!content) return;

  const externalLinks = content.querySelectorAll('a[href^="http"]');
  const newWindowText =
    locale.value === "en" ? "opens in new window" : "se abre en nueva ventana";

  externalLinks.forEach((link) => {
    if (!link.hasAttribute("rel")) {
      link.setAttribute("rel", "noopener noreferrer");
    }

    if (!link.hasAttribute("target")) {
      link.setAttribute("target", "_blank");
    }

    if (!link.querySelector(".external-icon")) {
      const icon = document.createElement("span");
      icon.className = "external-icon";
      icon.setAttribute("aria-hidden", "true");
      icon.innerHTML = " ↗";
      link.appendChild(icon);
    }

    const currentLabel = link.getAttribute("aria-label") || link.textContent;
    if (!currentLabel.includes("se abre") && !currentLabel.includes("opens")) {
      link.setAttribute("aria-label", `${currentLabel}, ${newWindowText}`);
    }
  });
};

/**
 * Maneja la navegación suave a anclas internas
 */
const handleInternalLinks = () => {
  if (typeof document === "undefined") return;
  const content = document.querySelector(".dynamic-content");
  if (!content) return;

  const internalLinks = content.querySelectorAll('a[href^="#"]');

  internalLinks.forEach((link) => {
    const handler = (e) => {
      e.preventDefault();
      const targetId = link.getAttribute("href").substring(1);
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        const originalTabIndex = targetElement.getAttribute("tabindex");
        if (!originalTabIndex) {
          targetElement.setAttribute("tabindex", "-1");
        }
        targetElement.focus();

        if (!originalTabIndex) {
          const timeoutId = setTimeout(() => {
            targetElement.removeAttribute("tabindex");
          }, 1000);
          timeoutIds.push(timeoutId);
        }
      }
    };

    link.addEventListener("click", handler);
    internalLinkHandlers.push({ element: link, handler });
  });
};
</script>

<style scoped>
/* ===== Page Structure ===== */
.privacy-page {
  min-height: 100%;
}

.main-content {
  padding: 16px 0 24px;
}

/* ========== CARD PRINCIPAL ========== */
.content-card {
  background: linear-gradient(135deg, #f8fafc 0%, #fff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

/* ========== CONTENIDO DINÁMICO ========== */
.dynamic-content {
  padding: 32px 40px;
  font-family: "Roboto", sans-serif;
  color: #333;
  line-height: 1.7;
}

/* ========== ENCABEZADOS ========== */
.dynamic-content :deep(h1),
.dynamic-content :deep(h2),
.dynamic-content :deep(h3) {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
  margin-top: 48px;
  margin-bottom: 24px;
  line-height: 1.3;
  scroll-margin-top: 100px;
}

.dynamic-content :deep(h1) {
  font-size: 2.25rem;
  border-bottom: 3px solid rgba(var(--v-theme-primary), 0.2);
  padding-bottom: 16px;
}

.dynamic-content :deep(h2) {
  font-size: 1.875rem;
}

.dynamic-content :deep(h3) {
  font-size: 1.5rem;
}

.dynamic-content :deep(h4) {
  color: #424242;
  font-weight: 500;
  font-size: 1.25rem;
  margin-top: 32px;
  margin-bottom: 16px;
  line-height: 1.4;
  scroll-margin-top: 100px;
}

.dynamic-content :deep(h5) {
  color: #616161;
  font-weight: 500;
  font-size: 1.125rem;
  margin-top: 24px;
  margin-bottom: 12px;
  line-height: 1.4;
}

/* ========== PÁRRAFOS ========== */
.dynamic-content :deep(p) {
  font-size: 1rem;
  line-height: 1.8;
  color: #424242;
  margin-bottom: 20px;
}

.dynamic-content :deep(p:last-child) {
  margin-bottom: 0;
}

/* ========== LISTAS ========== */
.dynamic-content :deep(ul),
.dynamic-content :deep(ol) {
  margin-left: 0;
  margin-bottom: 20px;
  padding-left: 28px;
}

.dynamic-content :deep(ul) {
  list-style-type: disc;
}

.dynamic-content :deep(ol) {
  list-style-type: decimal;
}

.dynamic-content :deep(li) {
  font-size: 1rem;
  line-height: 1.8;
  color: #424242;
  margin-bottom: 8px;
  padding-left: 8px;
}

.dynamic-content :deep(li::marker) {
  color: rgb(var(--v-theme-primary));
}

/* ========== ENLACES ========== */
.dynamic-content :deep(a) {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid rgba(var(--v-theme-primary), 0.3);
  padding-bottom: 1px;
  transition: all 0.2s ease;
}

.dynamic-content :deep(a:hover) {
  color: rgba(var(--v-theme-primary), 0.8);
  border-bottom-color: rgba(var(--v-theme-primary), 0.6);
  background-color: rgba(var(--v-theme-primary), 0.04);
}

.dynamic-content :deep(a:focus-visible) {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 3px;
  border-radius: 2px;
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.dynamic-content :deep(.external-icon) {
  font-size: 0.875em;
  opacity: 0.7;
  margin-left: 2px;
}

/* ========== SECCIONES Y ARTÍCULOS ========== */
.dynamic-content :deep(article) {
  margin-bottom: 56px;
}

.dynamic-content :deep(section) {
  margin-bottom: 64px;
  scroll-margin-top: 100px;
}

.dynamic-content :deep(section:last-child) {
  margin-bottom: 0;
}

/* ========== ELEMENTOS ESPECIALES ========== */
.dynamic-content :deep(strong),
.dynamic-content :deep(b) {
  color: #212121;
  font-weight: 600;
}

.dynamic-content :deep(em),
.dynamic-content :deep(i) {
  font-style: italic;
  color: #424242;
}

.dynamic-content :deep(code) {
  background-color: rgba(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
  padding: 2px 6px;
  border-radius: 4px;
  font-family: "Courier New", Courier, monospace;
  font-size: 0.875em;
}

.dynamic-content :deep(blockquote) {
  border-left: 4px solid rgb(var(--v-theme-primary));
  padding: 16px 24px;
  margin: 24px 0;
  background-color: rgba(var(--v-theme-primary), 0.04);
  border-radius: 0 8px 8px 0;
  font-style: italic;
  color: #616161;
}

.dynamic-content :deep(hr) {
  border: none;
  border-top: 2px solid rgba(0, 0, 0, 0.08);
  margin: 40px 0;
}

/* ========== TABLAS ========== */
.dynamic-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 24px 0;
  font-size: 0.938rem;
}

.dynamic-content :deep(th),
.dynamic-content :deep(td) {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.dynamic-content :deep(th) {
  background-color: rgba(var(--v-theme-primary), 0.08);
  color: #212121;
  font-weight: 600;
}

.dynamic-content :deep(tr:hover) {
  background-color: rgba(var(--v-theme-primary), 0.02);
}

/* ========== RESPONSIVE ========== */
@media (max-width: 960px) {
  .main-content {
    padding: 20px 24px;
  }

  .dynamic-content {
    padding: 28px 32px;
  }

  .dynamic-content :deep(h1) {
    font-size: 2rem;
  }

  .dynamic-content :deep(h2) {
    font-size: 1.625rem;
  }

  .dynamic-content :deep(h3) {
    font-size: 1.375rem;
  }

  .dynamic-content :deep(h4) {
    font-size: 1.125rem;
  }

  .dynamic-content :deep(p),
  .dynamic-content :deep(li) {
    font-size: 0.938rem;
  }
}

@media (max-width: 768px) {
  .main-content {
    padding: 16px;
  }

  .content-card {
    border-radius: 12px;
  }
}

@media (max-width: 600px) {
  .dynamic-content {
    padding: 24px 20px;
  }

  .dynamic-content :deep(h1) {
    font-size: 1.75rem;
    margin-top: 32px;
    margin-bottom: 20px;
  }

  .dynamic-content :deep(h2) {
    font-size: 1.5rem;
    margin-top: 32px;
    margin-bottom: 16px;
  }

  .dynamic-content :deep(h3) {
    font-size: 1.25rem;
    margin-top: 24px;
    margin-bottom: 12px;
  }

  .dynamic-content :deep(h4) {
    font-size: 1.063rem;
    margin-top: 20px;
    margin-bottom: 10px;
  }

  .dynamic-content :deep(p),
  .dynamic-content :deep(li) {
    font-size: 0.875rem;
  }

  .dynamic-content :deep(ul),
  .dynamic-content :deep(ol) {
    padding-left: 20px;
  }

  .dynamic-content :deep(blockquote) {
    padding: 12px 16px;
    margin: 16px 0;
  }

  .dynamic-content :deep(article) {
    margin-bottom: 40px;
  }

  .dynamic-content :deep(section) {
    margin-bottom: 48px;
  }
}

/* ========== ACCESIBILIDAD ========== */
@media (prefers-reduced-motion: reduce) {
  .dynamic-content :deep(a) {
    transition: none !important;
  }

  * {
    scroll-behavior: auto !important;
  }
}

@media (prefers-contrast: high) {
  .content-card {
    border: 2px solid rgba(0, 0, 0, 0.2);
  }

  .dynamic-content :deep(h1),
  .dynamic-content :deep(h2),
  .dynamic-content :deep(h3) {
    color: #000000;
  }

  .dynamic-content :deep(h1) {
    border-bottom-width: 4px;
    border-bottom-color: #000;
  }

  .dynamic-content :deep(h4),
  .dynamic-content :deep(h5),
  .dynamic-content :deep(p),
  .dynamic-content :deep(li),
  .dynamic-content :deep(strong),
  .dynamic-content :deep(b) {
    color: #000000;
  }

  .dynamic-content :deep(a) {
    font-weight: 600;
    border-bottom-width: 2px;
  }

  .dynamic-content :deep(blockquote) {
    border-left-width: 6px;
    background-color: rgba(0, 0, 0, 0.05);
  }

  .dynamic-content :deep(hr) {
    border-top-width: 3px;
    border-top-color: #000;
  }

  .dynamic-content :deep(th) {
    background-color: rgba(0, 0, 0, 0.1);
  }
}

@media print {
  .content-card {
    box-shadow: none !important;
    border: 1px solid #000;
  }

  .dynamic-content {
    padding: 20px;
  }

  .dynamic-content :deep(a) {
    color: #000;
    text-decoration: underline;
  }

  .dynamic-content :deep(a::after) {
    content: " (" attr(href) ")";
    font-size: 0.8em;
    font-weight: normal;
  }

  .dynamic-content :deep(article),
  .dynamic-content :deep(section) {
    page-break-inside: avoid;
  }

  .dynamic-content :deep(h1),
  .dynamic-content :deep(h2),
  .dynamic-content :deep(h3),
  .dynamic-content :deep(h4) {
    page-break-after: avoid;
  }
}
</style>
